import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { formatNumber } from "@/lib/format";

const REASONS = ["Sayım farkı", "Hasarlı / Fire", "Kayıp", "Bulunan", "Müşteri iadesi", "Diğer"];

export default function StockAdjustDialog({ row, onClose, onDone }) {
  const [mode, setMode] = useState("delta");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("Sayım farkı");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [idemKey] = useState(() => crypto.randomUUID());
  const n = Number(qty);
  const after = qty === "" ? null : mode === "set" ? n : row.physical + n;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/stock/adjustments", { product_id: row.product_id, warehouse_id: row.warehouse_id, mode, qty: n, reason, note: note || null },
        { headers: { "Idempotency-Key": idemKey } });
      toast.success("Stok düzeltmesi kaydedildi");
      onDone();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="stock-adjust-dialog">
        <DialogHeader><DialogTitle>Stok Düzeltme</DialogTitle></DialogHeader>
        <div className="text-[13px] text-slate-600 -mt-2"><span className="font-mono font-semibold">{row.sku}</span> · {row.name}<div className="text-xs text-slate-400">{row.warehouse_name}</div></div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          {[["Fiziki", row.physical], ["Rezerve", row.reserved], ["Kullanılabilir", row.available]].map(([l, v]) => (
            <div key={l} className="rounded border border-slate-200 py-2"><div className="text-slate-400">{l}</div><div className="font-mono font-semibold text-slate-900">{formatNumber(v)}</div></div>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">İşlem</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="stock-adjust-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="delta">Artır / Azalt (±)</SelectItem><SelectItem value="set">Sayım sonucu (yeni fiziki)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">{mode === "set" ? "Sayılan miktar" : "Miktar (örn. -5)"}</Label>
              <Input type="number" step="1" required value={qty} onChange={(e) => setQty(e.target.value)} data-testid="stock-adjust-qty" />
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Neden</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="stock-adjust-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Açıklama</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="stock-adjust-note" /></div>
          {after !== null && !Number.isNaN(after) && (
            <div className="text-xs text-slate-500" data-testid="stock-adjust-preview">Yeni fiziki: <b className="font-mono text-slate-900">{formatNumber(after)}</b> · Yeni kullanılabilir: <b className="font-mono text-slate-900">{formatNumber(after - row.reserved)}</b></div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="stock-adjust-cancel">Vazgeç</Button>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="stock-adjust-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Kaydet</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
